import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  Download, 
  Upload, 
  Database, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  RefreshCw,
  Info,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`
  };
};

const BackupRestore = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [backupInfo, setBackupInfo] = useState(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [finalConfirmDialogOpen, setFinalConfirmDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  useEffect(() => {
    loadBackupInfo();
  }, []);

  const loadBackupInfo = async () => {
    setIsLoadingInfo(true);
    try {
      const response = await fetch(`${API_URL}/api/backup/info`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to load backup info');
      }
      
      const data = await response.json();
      setBackupInfo(data);
    } catch (error) {
      console.error('Failed to load backup info:', error);
      toast({
        title: 'Error',
        description: 'Failed to load backup information',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingInfo(false);
    }
  };

  const handleExportBackup = async () => {
    setIsExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/backup/export`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Export failed');
      }

      // Get the blob data
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'myvenuemate_backup.json';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: `Backup exported successfully: ${filename}`,
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export backup',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast({
          title: 'Invalid File',
          description: 'Please select a JSON backup file',
          variant: 'destructive'
        });
        return;
      }
      setSelectedFile(file);
      setConfirmDialogOpen(true);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!selectedFile) return;
    
    setConfirmDialogOpen(false);
    setFinalConfirmDialogOpen(true);
  };

  const handleFinalRestore = async () => {
    if (!selectedFile) return;
    
    setFinalConfirmDialogOpen(false);
    setIsRestoring(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/backup/restore`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Restore failed');
      }

      const data = await response.json();

      toast({
        title: 'Restore Successful',
        description: `Backup restored successfully! Restored: ${Object.entries(data.restored_counts || {}).map(([k,v]) => `${k}: ${v}`).join(', ')}`,
      });

      // Reload backup info
      await loadBackupInfo();

      // Reset file input
      setSelectedFile(null);
      setFileInputKey(Date.now());

    } catch (error) {
      console.error('Restore failed:', error);
      toast({
        title: 'Restore Failed',
        description: error.message || 'Failed to restore backup',
        variant: 'destructive'
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const cancelRestore = () => {
    setConfirmDialogOpen(false);
    setFinalConfirmDialogOpen(false);
    setSelectedFile(null);
    setFileInputKey(Date.now());
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="hover:bg-gray-100 dark:bg-secondary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
            <Database className="w-6 h-6 text-teal-600" />
            Backup & Restore
          </h1>
          <p className="text-gray-500 dark:text-muted-foreground text-sm mt-1">
            Export your data or restore from a previous backup
          </p>
        </div>
      </div>

      {/* Warning Alert */}
      <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Important:</strong> Restoring a backup will replace your current data. 
          Make sure to export a backup of your current data before restoring.
        </AlertDescription>
      </Alert>

      {/* Current Data Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg dark:text-foreground">
            <Info className="w-5 h-5 text-blue-600" />
            Current Data Overview
          </CardTitle>
          <CardDescription>
            Data that will be included in your backup
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingInfo ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
              <span className="ml-2 text-gray-600 dark:text-muted-foreground">Loading...</span>
            </div>
          ) : backupInfo ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {backupInfo.data_counts && Object.entries(backupInfo.data_counts).map(([key, count]) => (
                  <div key={key} className="bg-gray-50 dark:bg-secondary rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-teal-600">{count}</p>
                    <p className="text-sm text-gray-600 dark:text-muted-foreground capitalize">{key}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>Last checked: {new Date(backupInfo.timestamp).toLocaleString()}</span>
              </div>
              {backupInfo.is_master && (
                <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 dark:bg-purple-900/20 p-2 rounded">
                  <Shield className="w-4 h-4" />
                  <span>Master account - Full database access</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-muted-foreground text-center py-4">Unable to load data info</p>
          )}
        </CardContent>
      </Card>

      {/* Export Section */}
      <Card className="border-2 border-green-200 bg-green-50 dark:bg-green-900/20/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-green-800 dark:text-foreground">
            <Download className="w-5 h-5" />
            Export Backup
          </CardTitle>
          <CardDescription>
            Download all your data as a JSON file
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 text-sm text-green-700 bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">What&apos;s included in the backup:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>All bookings and payment records</li>
                <li>All leads and follow-up history</li>
                <li>Venue information</li>
                <li>Inventory items</li>
                <li>Notifications and activity logs</li>
                {backupInfo?.is_master && (
                  <>
                    <li className="text-purple-700 font-medium">All admin accounts and their staff</li>
                    <li className="text-purple-700 font-medium">All organizations&apos; complete data</li>
                    <li className="text-purple-700 font-medium">Payment orders and subscriptions</li>
                  </>
                )}
              </ul>
            </div>
          </div>
          
          <Button
            onClick={handleExportBackup}
            disabled={isExporting}
            className="w-full md:w-auto bg-green-600 hover:bg-green-700"
          >
            {isExporting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Backup
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Restore Section */}
      <Card className="border-2 border-orange-200 bg-orange-50 dark:bg-orange-900/20/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-orange-800 dark:text-foreground">
            <Upload className="w-5 h-5" />
            Restore from Backup
          </CardTitle>
          <CardDescription>
            Upload a previously exported backup file to restore your data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 text-sm text-orange-700 bg-orange-100 dark:bg-orange-900/30 p-3 rounded-lg">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Warning - This action will:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Replace all your current bookings with backup data</li>
                <li>Replace all your current leads with backup data</li>
                <li>Replace venue and inventory information</li>
                <li>This action cannot be undone!</li>
              </ul>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              key={fileInputKey}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              id="backup-file"
            />
            <label
              htmlFor="backup-file"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isRestoring 
                  ? 'border-gray-300 dark:border-border bg-gray-100 dark:bg-secondary cursor-not-allowed' 
                  : 'border-orange-300 hover:border-orange-400 hover:bg-orange-100 dark:bg-orange-900/30'
              }`}
            >
              {isRestoring ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin text-orange-600" />
                  <span className="text-orange-700 font-medium">Restoring...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-orange-600" />
                  <span className="text-orange-700 font-medium">Choose Backup File (.json)</span>
                </>
              )}
            </label>
          </div>
        </CardContent>
      </Card>

      {/* First Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="w-5 h-5" />
              Confirm Restore
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>You are about to restore from:</p>
              <p className="font-medium text-gray-900 dark:text-foreground">{selectedFile?.name}</p>
              <p className="text-orange-600">This will replace your current data. Are you sure?</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelRestore}>
              Cancel
            </Button>
            <Button 
              onClick={handleRestoreConfirm}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final Confirmation Dialog */}
      <Dialog open={finalConfirmDialogOpen} onOpenChange={setFinalConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Final Confirmation
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p className="text-red-600 font-medium">
                ⚠️ THIS ACTION CANNOT BE UNDONE!
              </p>
              <p>
                All your current data will be permanently replaced with the backup data.
                Make sure you have exported a backup of your current data first.
              </p>
              <p className="font-medium">
                Type &quot;RESTORE&quot; to confirm (or click the button below):
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelRestore}>
              Cancel
            </Button>
            <Button 
              onClick={handleFinalRestore}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Restore Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupRestore;
